import { useSelector, useDispatch } from "react-redux";
import { Dialog } from "primereact/dialog";
import { toggleDialog } from "../../../../redux/Slices/Global/globalSlice";
import { useGetCampaignContent } from "../../../../queries";
import { useEffect, useMemo, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import ContentList from "./ContentList";
import ContentPreview from "./ContentPreview";
import { useQueryClient } from "react-query";
import { setPaused } from "../../../../redux/Slices/deviceSlice";
import { useTranslation } from "react-i18next";
import { resolveDeviceOnline, resolveDeviceStatus } from "../../../../utils";
import DeviceLastLoginListener from "../../../Services/DeviceLastLoginListener";

// Premium Status Components matching Show.jsx design
const PreviewStatusOverlay = ({ type, message }) => {
  const isBlackout = type === "blackout";
  return (
    <div className={`absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-4 overflow-hidden z-[70] ${isBlackout ? 'bg-black' : 'bg-slate-900 border border-slate-800'}`}>
      <div className={`noise-panel ${isBlackout ? 'opacity-5' : 'opacity-10'}`}></div>
      <div className="relative z-10 flex flex-col items-center gap-6">
        {isBlackout ? (
          <div className="w-12 h-12 rounded-full border-4 border-white/10 border-t-white/40 animate-spin shadow-[0_0_20px_rgba(255,255,255,0.1)]"></div>
        ) : (
          <div className="flex gap-2">
            {[0, 0.1, 0.2].map(d => (
              <div key={d} className="w-2 h-8 bg-indigo-500 rounded-full animate-bounce shadow-[0_0_15px_rgba(99,102,241,0.4)]" style={{ animationDelay: `${d}s` }}></div>
            ))}
          </div>
        )}
        <h3 className="text-white/80 text-sm font-black tracking-[0.2em] uppercase flicker">{message}</h3>
      </div>
    </div>
  );
};

const PreviewOfflineOverlay = () => {
  const { t } = useTranslation();
  return (
    <div className="absolute inset-0 w-full h-full bg-black flex flex-col items-center justify-center overflow-hidden z-[70]">
      <div className="noise-panel opacity-25"></div>
      <div className="scan-line"></div>
      <div className="relative z-10 flex flex-col items-center gap-6">
        <div className="w-20 h-20 rounded-[2rem] bg-red-950/30 flex items-center justify-center border-2 border-red-500/40 text-red-500 shadow-2xl flicker">
          <i className="fa-solid fa-plane-slash text-3xl"></i>
        </div>
        <div className="text-center">
          <div className="text-[10px] font-black text-red-500 uppercase tracking-[0.2em] animate-pulse mb-1">{t("devices.preview_signal_lost")}</div>
          <span className="text-white/60 text-[10px] font-bold uppercase tracking-widest">{t("devices.preview_hardware_offline")}</span>
        </div>
      </div>
    </div>
  );
};

function DevicePreview({ campaignTag, currentMediaPlaying, currentBannerPlaying, currentPlaygroundPlaying, currentCornerPlaying, currentCampaignId, name, degree = 0 }) {
  const { t } = useTranslation();
  const openDialog = useSelector((state) => state.global.openDialog);
  const { selectedDevice } = useSelector((state) => state.device);
  const deviceId = selectedDevice.id;
  const [campaignId, setCampaignId] = useState(null);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const isOnline = useMemo(() => resolveDeviceOnline(selectedDevice?.connectivity, selectedDevice?.online), [selectedDevice?.connectivity, selectedDevice?.online]);
  // Phase 3: content preview / "signal lost" must gate on actually-playing
  // vs actually-offline, not mere reachability — a standby device is
  // isOnline=true (reachable) but blanked, so it should show neither the
  // (stale) content preview nor the alarming offline overlay.
  const deviceStatus = useMemo(() => resolveDeviceStatus(selectedDevice?.connectivity, selectedDevice?.presence_status, selectedDevice?.online), [selectedDevice?.connectivity, selectedDevice?.presence_status, selectedDevice?.online]);

  const rotationDegree = Number(degree) || 0;
  const isPortrait = rotationDegree === 90 || rotationDegree === 270;

  useEffect(() => {
    if (currentCampaignId && currentCampaignId !== campaignId) {
      setCampaignId(currentCampaignId);
    }
  }, [currentCampaignId, campaignId]);

  const { isLoading, data } = useGetCampaignContent(campaignId, {
    enabled: !!campaignId,
  });

  const onclose = () => {
    queryClient.removeQueries(["getCampaignContent", campaignId, deviceId]);
    dispatch(toggleDialog("devicePreview"));
  };

  const header = () => (
    <div className="flex items-center justify-between w-full pr-8">
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-inner ${isOnline ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-400'}`}>
          <i className={`fa-solid ${campaignTag === "scheduled" ? 'fa-calendar-day' : campaignTag === "prayer" ? 'fa-mosque' : 'fa-bolt'} text-lg`}></i>
        </div>
        <div className="flex flex-col">
          <h2 className="text-lg font-black text-slate-800 m-0 tracking-tight">
            {name || t("devices.preview_campaign_stream")}
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">{t("devices.preview_target", { name: selectedDevice.name })}</span>
            <span className="w-1 h-1 rounded-full bg-slate-300"></span>
            <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest leading-none">{t("devices.preview_rotation", { degree: rotationDegree })}</span>
          </div>
        </div>
      </div>
      
      <div className="hidden sm:flex items-center gap-3 bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100">
        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`}></div>
        <span className={`text-[10px] font-black uppercase tracking-widest ${isOnline ? 'text-green-600' : 'text-slate-400'}`}>
          {isOnline ? t("devices.preview_live_synchronization") : t("devices.preview_hardware_offline")}
        </span>
      </div>
    </div>
  );

  useEffect(() => {
    return () => {
      dispatch(setPaused(false));
    };
  }, [dispatch]);

  return (
    <Dialog
      visible={openDialog.devicePreview}
      onHide={onclose}
      draggable={false}
      modal
      dismissableMask
      className="premium-dialog rounded-[2.5rem] overflow-hidden border-none"
      header={header}
      headerClassName="bg-white/80 backdrop-blur-xl border-b border-slate-100 py-4 px-6"
      contentClassName="p-0 bg-slate-50/30"
      style={{ width: '85vw', maxWidth: 1200 }}
      breakpoints={{ '960px': '95vw' }}
    >
      {/* Own presence listener — this dialog was previously relying on
          whichever DeviceCard happened to render it having a listener
          mounted; harden it so it doesn't go stale if opened another way. */}
      {selectedDevice?.serial_name && (
        <DeviceLastLoginListener serialName={selectedDevice.serial_name} path={`Devices/${selectedDevice.serial_name}/presence`} />
      )}
      <div className="flex flex-col lg:flex-row h-full max-h-[85vh] p-6 gap-6">
        {/* LEFT: Hardware Simulation Frame */}
        <div className="flex-[1.2] flex flex-col gap-4 items-center justify-center min-h-[500px]">
          <div 
            className="relative group bg-black rounded-[2rem] overflow-hidden shadow-2xl ring-8 ring-slate-900 transition-all duration-700 ease-in-out"
            style={{
                aspectRatio: isPortrait ? '9/16' : '16/9',
                width: isPortrait ? 'auto' : '100%',
                height: isPortrait ? 'min(65vh, 650px)' : 'auto',
                maxHeight: '65vh'
            }}
          >
            {/* CONTENT: Automatically fits the container aspect ratio WITHOUT additional rotation */}
            <div className="absolute inset-0 w-full h-full z-10 overflow-hidden bg-slate-950">
              {deviceStatus === 'online' && name !== 'Prayer break' && name !== 'Downloading...' && (
                <ContentPreview
                    campaignData={data}
                    currentBannerPlaying={currentBannerPlaying}
                    currentCornerPlaying={currentCornerPlaying}
                    currentPlaygroundPlaying={currentPlaygroundPlaying}
                    currentMediaPlaying={currentMediaPlaying}
                    campaignId={campaignId}
                    deviceId={deviceId}
                />
              )}
            </div>

            {/* OVERLAYS: FIXED Direction matching Show.jsx */}
            <div className="absolute inset-0 w-full h-full z-50 pointer-events-none">
               {(deviceStatus === 'offline' || deviceStatus === 'scheduled_off') && <div className="pointer-events-auto w-full h-full"><PreviewOfflineOverlay /></div>}
               {deviceStatus === 'online' && name === 'Prayer break' && (
                 <div className="pointer-events-auto w-full h-full">
                    <PreviewStatusOverlay type="blackout" message={t("devices.preview_prayer_break")} />
                 </div>
               )}
               {deviceStatus === 'online' && name === 'Downloading...' && (
                 <div className="pointer-events-auto w-full h-full">
                    <PreviewStatusOverlay type="gray" message={t("devices.preview_downloading_assets")} />
                 </div>
               )}
               {deviceStatus === 'online' && isLoading && (
                 <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm z-[80] pointer-events-auto">
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                    <span className="text-[10px] font-black text-white mt-4 uppercase tracking-widest animate-pulse">{t("devices.preview_syncing_hardware")}</span>
                 </div>
               )}
            </div>
          </div>
          
          <div className="w-full max-w-sm bg-slate-100/50 rounded-2xl p-3 border border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`}></div>
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                {isPortrait ? t("devices.preview_portrait_orientation") : t("devices.preview_landscape_orientation")}
              </span>
            </div>
            <span className="bg-white px-2 py-0.5 rounded border border-slate-200 text-[9px] font-bold text-slate-400 uppercase">{t("devices.preview_synchronized")}</span>
          </div>
        </div>

        {/* RIGHT: Content Feed */}
        <div className="flex-1 w-full lg:min-w-[360px] flex flex-col bg-white border border-slate-200 rounded-[2rem] overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <h4 className="m-0 text-slate-800 font-black text-xs uppercase tracking-widest">{t("devices.preview_media_inventory")}</h4>
            <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 text-[10px] font-bold">{t("devices.preview_live_badge")}</span>
          </div>
          
          <div className="flex-1 overflow-y-auto p-2">
            {isLoading ? (
              <div className="flex flex-col h-full items-center justify-center py-12 gap-4">
                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("devices.preview_hydrating_list")}</span>
              </div>
            ) : (
              <ContentList
                isOnline={isOnline}
                isLoading={isLoading}
                data={data}
                currentMediaPlaying={currentMediaPlaying}
                currentBannerPlaying={currentBannerPlaying}
                currentCornerPlaying={currentCornerPlaying}
                currentPlaygroundPlaying={currentPlaygroundPlaying}
              />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default DevicePreview;
